import clsx from 'clsx';
import { type ChangeEvent, forwardRef, useRef } from 'react';

import { _mergeProps } from '@dynatrace/strato-components/core';

import { useButtonGroupContext } from './hooks/useButtonGroupContext.js';
import * as styles from './ToggleButtonGroup.sty.js';
import type { ToggleButtonGroupItemProps } from './types.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { RadioProps } from '../../forms/radio/Radio.js';

/**
 * ToggleButton group item component.
 * @public
 */
export const ToggleButtonGroupItem = forwardRef<
  HTMLLabelElement,
  ToggleButtonGroupItemProps
>((props, forwardedRef) => {
  const ref = useRef<HTMLInputElement>(null);
  const { state } = useButtonGroupContext();
  const {
    disabled: isDisabled = false,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const radioProps: RadioProps = {
    ...remainingProps,
    id: state?.id,
    disabled: state?.isDisabled ? true : isDisabled,
    children,
  };

  const radioValue = radioProps.value as string;
  const checked = state?.selectedValue === radioValue;
  const tabIndex =
    (radioValue === state?.selectedValue && !radioProps.disabled) ||
    (state?.selectedIsDisabled && radioValue === state?.firstFocusableValue) ||
    state?.lastFocusedValue === null
      ? 0
      : -1;

  const { focusClassName, focusProps } = useFocusRing({
    disabled: radioProps.disabled,
    color: !radioProps.disabled && checked ? 'primary' : 'neutral',
  });

  return (
    <label
      data-testid={dataTestId}
      ref={forwardedRef}
      className={clsx(
        consumerClassName,
        styles.buttonGroupItem({
          disabled: radioProps.disabled,
          checked: checked,
        }),
        focusClassName,
      )}
      style={consumerStyle}
      {...focusProps}
    >
      <VisuallyHidden>
        <input
          type="radio"
          name={radioProps.id}
          value={radioValue}
          disabled={radioProps.disabled}
          checked={checked}
          tabIndex={tabIndex}
          {..._mergeProps(focusProps, {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              if (radioValue !== undefined) {
                state?.onChange?.(radioValue, event);
                state?.setSelectedValue(radioValue);
              }
            },
          })}
          ref={ref}
        />
      </VisuallyHidden>
      {children}
    </label>
  );
});
